import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { RuntimeConfigService } from './runtime-config.service';
import { defer, Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { User } from '../app/models/user.model';
import { PostRecipientHousehold } from '../app/models/post-recipient-household.model';
import { GuestUserRequest } from '../app/models/guest-user-request.model';
import { Device } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';
import { UserTypeEnum } from '../app/models/user-type.enum';
import { JobFunction } from '../app/models/JobFunction';
import { GradeLevelOption, ImpersonationUserSearchRequest, ImpersonationUserSearchResult } from '../app/models/ImpersonationUserSearch';
import { PaginationModel } from '../app/apps/shared/pagination-model';
import { IdentitySwitchService } from './identity-switch.service';

@Injectable({
  providedIn: 'root'
})

export class UserProfileService {
  private apiUrl: string;

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(private http: HttpClient,
              private runtimeConfig: RuntimeConfigService,
              private identitySwitch: IdentitySwitchService) {
    this.apiUrl = this.runtimeConfig.currentConfig.apiUrl + '/UserProfile';

    // Subscribe to config changes to update apiUrl
    this.runtimeConfig.config$.subscribe(config => {
      this.apiUrl = config.apiUrl + '/UserProfile';
    });
  }

  clearImpersonation(): Observable<User> {
    // defer so the gate goes up on subscribe, i.e. strictly before the request that reverts the
    // identity server-side. Everything parent-portal-scoped already in flight is aborted and
    // nothing new is issued until the client has adopted the profile this returns — otherwise the
    // still-running page keeps calling the guardian endpoints as the staff id it just became.
    return defer(() => {
      this.identitySwitch.begin();
      return this.http.post<User>(`${this.apiUrl}/ClearImpersonationAsync`, { });
    }).pipe(
      // A failed call means the identity did NOT change, so the hold has to come down here rather
      // than at the next setCurrentUserProfile(): the impersonate page navigates by router instead
      // of reloading, so a stranded gate would suppress parent-portal calls for the rest of the
      // SPA session.
      tap({ error: () => this.identitySwitch.end() }),
      // The parent-account flag outlives the impersonation it describes unless it is dropped
      // here. Callers clear impersonation both when stopping and when starting a new one, so
      // without this a "Switch to Parent account" leaves the header stuck on the parent banner
      // for whoever is impersonated next.
      // finalize rather than tap: the impersonate page starts a new impersonation even when this
      // call fails, so the flag has to go on the error path too.
      finalize(() => sessionStorage.removeItem('parent_account_switch'))
    );
  }

  getGuardiansBySchoolId(schoolId: number): Observable<User[]> {
    return this.http.get<User[]>(`${this.apiUrl}/GetGuardianUserProfilesBySchoolIdAsync/${schoolId}`);
  }
  
  getImpersonatedUserProfile(): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/GetImpersonatedUserProfileAsync`);
  }

  getGradeLevels(): Observable<GradeLevelOption[]> {
    return this.http.get<GradeLevelOption[]>(`${this.apiUrl}/GetGradeLevelsAsync`);
  }

  getJobFunctions(): Observable<JobFunction[]> {
    return this.http.get<JobFunction[]>(`${this.apiUrl}/GetJobFunctionsAsync`);
  }

  searchImpersonationUsers(request: ImpersonationUserSearchRequest): Observable<PaginationModel<ImpersonationUserSearchResult>> {
    return this.http.post<PaginationModel<ImpersonationUserSearchResult>>(`${this.apiUrl}/SearchImpersonationUsersAsync`, request);
  }

  getRecentImpersonationSearchResults(): Observable<ImpersonationUserSearchResult[]> {
    return this.http.get<ImpersonationUserSearchResult[]>(`${this.apiUrl}/GetRecentImpersonationSearchResultsAsync`);
  }

  getStaffBySchoolId(schoolId: number): Observable<User[]> {
    return this.http.get<User[]>(`${this.apiUrl}/GetStaffUserProfilesBySchoolIdAsync/${schoolId}`);
  }
  
  getStudentsBySchoolId(schoolId: number): Observable<User[]> {
    return this.http.get<User[]>(`${this.apiUrl}/GetStudentUserProfilesBySchoolIdAsync/${schoolId}`);
  }

  getUserProfileByOktaUserId(): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/GetUserProfileByOktaUserIdAsync`);
  }

  getUserProfileByUserId(userId: number): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/GetUserProfileByUserIdAsync/${userId}`);
  }

  getActualUserProfileByUserId(userId: number): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/GetActualUserProfileByUserIdAsync/${userId}`);
  }

  getUserProfilesByUserIds(userIds: number[]): Observable<User[]> {
    return this.http.get<User[]>(`${this.apiUrl}/GetUserProfilesByUserIdAsync/${userIds.join(',')}`);
  }

  impersonateUser(userId: number, firstName: string, lastName: string, userType: string, schoolName: string): Observable<User> {
    const params = new URLSearchParams();
    params.append('firstName', firstName);
    params.append('lastName', lastName);
    params.append('userType', userType);
    params.append('schoolName', schoolName);
    
    const url = `${this.apiUrl}/ImpersonateUserAsync/${userId}?${params.toString()}`;

    // Same transition window in the other direction: the server adopts the impersonated identity
    // on return, so hold parent-portal calls until the client is running as that user.
    return defer(() => {
      this.identitySwitch.begin();
      return this.http.post<User>(url, { });
    }).pipe(
      // Nothing changed on a failure — release the hold (see clearImpersonation).
      tap({ error: () => this.identitySwitch.end() })
    );
  }

  importUserProfile(userProfile: User): Observable<User> {
    return this.http.post<User>(`${this.apiUrl}/ImportUserProfileAsync/`, userProfile);
  }

  findOrCreateGuestUser(request: GuestUserRequest): Observable<User> {
    return this.http.post<User>(`${this.apiUrl}/FindOrCreateGuestUserAsync`, request);
  }

  getGuardianUsersByStudentEID(studentEID: number): Observable<User[]> {
    return this.http.get<User[]>(`${this.apiUrl}/GetGuardianUsersByStudentEIDAsync/${studentEID}`);
  }

  // Resolves a student's guardians without a school-access check (Quick Notice CSV import).
  getGuardianUsersByStudentEIDNoSchoolCheck(studentEID: number): Observable<User[]> {
    return this.http.get<User[]>(`${this.apiUrl}/GetGuardianUsersByStudentEIDNoSchoolCheckAsync/${studentEID}`);
  }

  public searchForUsers(
    searchString: string,
    userTypes: UserTypeEnum[],
    count?: number,
    schoolId?: number,
    excludeUserIds?: number[],
    showCurrentUser: boolean = true,
    useShowInDirectory: boolean = false,
    showStudentInactiveStatuses: boolean = true,
    isMessaging: boolean = false,
    postId?: number
  ): Observable<User[]> {
    let params = new HttpParams()
      .set('showCurrentUser', showCurrentUser)
      .set('useShowInDirectory', useShowInDirectory)
      .set('showStudentInactiveStatuses', showStudentInactiveStatuses)
      .set('isMessaging', isMessaging);
    if (postId !== undefined) {
      params = params.set('postId', postId.toString());
    }

    if (userTypes && userTypes.length > 0) {
      userTypes.forEach(t => params = params.append('userTypes', t.toString()));
    }
    if (count !== undefined) {
      params = params.set('count', count.toString());
    }
    if (schoolId !== undefined) {
      params = params.set('schoolId', schoolId.toString());
    }
    if (excludeUserIds && excludeUserIds.length > 0) {
      excludeUserIds.forEach(id => params = params.append('excludeUserIds', id.toString()));
    }

    return this.http.get<User[]>(`${this.apiUrl}/SearchForUsersAsync/${searchString}`, { params });
  }

  // Share-post flow: students whose guardians received the post, each with those guardians (staff only).
  public getPostRecipientHouseholds(postId: number): Observable<PostRecipientHousehold[]> {
    return this.http.get<PostRecipientHousehold[]>(`${this.apiUrl}/GetPostRecipientHouseholdsAsync/${postId}`);
  }

  async getUserCurrentMobileDeviceInformation (): Promise<any> {
    // check if the user is using a mobile device
    if(Capacitor.isNativePlatform() === false) {
      return null;
    } else {
      const deviceInfo = await Device.getInfo();
      const deviceId = (await Device.getId()).identifier;
      const deviceModelVersion = deviceInfo.model;
      const devicePlatform = deviceInfo.platform;
      const deviceOSVersion = deviceInfo.osVersion;
      const deviceManufacturerVersion = deviceInfo.manufacturer;
  
      return { deviceId, deviceModelVersion, devicePlatform, deviceOSVersion, deviceManufacturerVersion };
    }
  }

  savePushNotificationToken(userId: number,
    token: string,
    deviceId: string,
    deviceModelVersion: string,
    devicePlatform: string,
    deviceOSVersion: string,
    deviceManufacturerVersion: string): Observable<any> {
    
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      const body = { userId, token, deviceId, deviceModelVersion, devicePlatform, deviceOSVersion, deviceManufacturerVersion };
      return this.http.post<any>(`${this.apiUrl}/SavePushNotificationToken/`, body, { headers });
  }

  // delete the push notification token, parameters are token, deviceId
  deletePushNotificationToken(token: string) {
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      const body = { token };
      return this.http.post<any>(`${this.apiUrl}/DeletePushNotificationToken/`, body, { headers });
  }

  // device id may have been changed, so delete all push notifications with the old device id
  // delete push notification by device id
  deletePushNotificationTokenByDeviceId(deviceId: string) {
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      const body = { deviceId };
      return this.http.post<any>(`${this.apiUrl}/DeletePushNotificationTokenByDeviceId/`, body, { headers });
  }

  //fetch user preferences
  fetchUserPreferences(userId: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/GetUserPreferencesAsync/${userId}`);
  }

  getRecentImpersonations(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/GetRecentImpersonationsAsync`);
  }

  deleteRecentImpersonation(impersonationId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/DeleteRecentImpersonationAsync/${impersonationId}`);
  }

  isServiceCenterStaff(): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/IsServiceCenterStaffAsync`);
  }

}
